import React from 'react';
import { BarChart3, TrendingUp, Shield, Target, ArrowRight, Activity } from 'lucide-react';
import indicesImage from '../../assets/medium-shot-business-team-working_23-2149271692.jpg_nigaw2.jpg';

const IndicesPage = () => {
  const services = [
    {
      icon: BarChart3,
      title: 'Index Fund Management',
      description: 'We provide comprehensive index fund management services, helping you gain exposure to broad market segments through diversified index investments that track major market benchmarks.'
    },
    {
      icon: Activity,
      title: 'Performance Tracking',
      description: 'Our advanced tracking systems monitor your index investments in real-time, providing detailed performance analytics and ensuring your portfolio remains aligned with market indices.'
    },
    {
      icon: Target,
      title: 'Strategic Asset Allocation',
      description: 'We develop strategic asset allocation models that incorporate various indices to optimize your portfolio\'s risk-return profile and achieve long-term investment objectives.'
    },
    {
      icon: Shield,
      title: 'Risk Assessment',
      description: 'Our comprehensive risk assessment methodology evaluates index volatility and correlation patterns to ensure your investments maintain appropriate risk levels.'
    },
    {
      icon: TrendingUp,
      title: 'Market Analysis',
      description: 'Stay informed with our in-depth market analysis covering major global indices, sector performance, and economic indicators that drive index movements.'
    },
    {
      icon: BarChart3,
      title: 'Portfolio Optimization',
      description: 'We continuously optimize your index portfolio through rebalancing and strategic adjustments to maintain target allocations and maximize efficiency.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src={indicesImage}
            alt="Indices Investment"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Indices</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">Indices</span>
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Index Investment at <span className="text-gold-600">COREVEST LIMITED</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Index investing offers a powerful way to participate in broad market movements while maintaining diversification and cost efficiency. At COREVEST LIMITED, we specialize in index-based investment strategies that provide exposure to various market segments, sectors, and geographic regions through carefully selected index funds and ETFs.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our approach to index investing combines passive investment benefits with active portfolio management, ensuring optimal allocation and performance while minimizing costs and maximizing long-term growth potential.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src={indicesImage}
                  alt="Index Investment Strategy"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="mb-20">
            <div className="text-center mb-16">
              <h3 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                Comprehensive Index Services
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-4">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Invest in Indices?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Diversify your portfolio with strategic index investments and benefit from broad market exposure 
              with expert guidance and optimized allocation strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://online.amdconsults.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-full transition-colors duration-300"
              >
                Start Investing
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <button className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300">
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndicesPage; 